<template>
  <TaskPage no-brand no-back :no-action="!isCompleteRef" :can-continue="true" @action="onExplore">
    <template #title>Upload my tree</template>

    <SyncSourceHeader v-if="sourceRef" :source="sourceRef"/>

    <div v-if="isCompleteRef" class="ready">
      <div class="message">Your online tree is ready!</div>
      <div class="fireworks">
        <img src="@webapp/assets/fireworks.gif" alt=""/>
      </div>
    </div>

    <div v-else-if="!isFailedRef" class="progress-area">
      <ProgressBar :value="completedRef" :max="totalRef" smooth/>
      <div class="status">Creating online tree...</div>
    </div>

    <template #secondaryAction>
      <router-link v-if="syncRef?.viewGroupId" :to="DataGroup.getResearchPageUrl(syncRef.viewGroupId)" class="btn btn-outline-primary">
        Manage my tree
      </router-link>
    </template>
    <template #actionText>Explore my tree</template>

    <Modal ref="failedModalRef" info @hidden="onFailedDismissed">
      <template #title>Something went wrong</template>
      We couldn't create your online tree. Let's try again.
    </Modal>
  </TaskPage>
</template>

<style lang="scss" scoped>
.ready {
  margin-top: 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  .fireworks {
    position: relative;
    width: 100%;
    min-height: 200px;

    img {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
}

.progress-area {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .status {
    font-size: 0.875rem;
    color: #888;
  }
}
</style>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { usePageTitle } from '@webapp/util/AppUtil'
import { useSyncSessionStore } from './SyncSessionStore'
import { toSyncSource } from './SyncSessionModel'
import { useSyncStore } from '@webapp/rd/SyncStore'
import { useResearchDataNotifications } from '@webapp/rd/ResearchDataNotifications'
import { LoadMode } from '@webapp/util/AsyncData'
import { SyncState } from '@webapp/rd/ResearchDataModel'
import { DataGroup } from '@webapp/gp/GroupAdminModel'
import TaskPage from '@webapp/util/TaskPage.vue'
import ProgressBar from '@webapp/util/ProgressBar.vue'
import Modal from '@webapp/util/Modal.vue'
import SyncSourceHeader from './SyncSourceHeader.vue'

const props = defineProps<{
  sessionId?: string
}>()

const emit = defineEmits(['complete', 'failed'])

const router = useRouter()
const syncSessionStore = useSyncSessionStore()
const syncStore = useSyncStore()
const notifications = useResearchDataNotifications()

// Captured once from the session's state -- it outlives the session, which is torn down on completion.
const syncIdRef = ref<string>()
const syncRef = computed(() => syncStore.getAsyncSync(syncIdRef.value, LoadMode.EnsureLoaded)?.data)
const sourceRef = computed(() => syncRef.value ? toSyncSource(syncRef.value) : undefined)
const stateRef = computed(() => syncRef.value?.state)
const isCompleteRef = computed(() => stateRef.value === SyncState.Completed)
const isFailedRef = computed(() =>
  stateRef.value === SyncState.Failed || stateRef.value === SyncState.Cancelled)

const completedRef = ref(0)
const totalRef = ref(1)

const failedModalRef = ref<InstanceType<typeof Modal>>()

usePageTitle('Upload my tree')

notifications.onSyncProgress(args => {
  if (args.syncID === syncIdRef.value) {
    completedRef.value = args.completed
    totalRef.value = args.total
  }
})

notifications.onSyncStateChanged(args => {
  if (args.itemId === syncIdRef.value) {
    syncStore.getAsyncSync(args.itemId, LoadMode.Reload)
  }
})

// A failure sends the user back to Options with an explanation, where they can adjust and retry.
watch(isFailedRef, (failed) => {
  if (failed) {
    failedModalRef.value?.open()
  }
})

watch(isCompleteRef, (complete) => {
  if (complete) {
    syncSessionStore.destroyCompletedSessionAsync(props.sessionId)
    emit('complete')
  }
})

onMounted(() => {
  syncIdRef.value = syncSessionStore.getState(props.sessionId)?.syncId
  if (syncIdRef.value) {
    syncStore.getAsyncSync(syncIdRef.value, LoadMode.Reload)
  }
})

function onExplore() {
  router.push('/home')
}

function onFailedDismissed() {
  emit('failed')
}
</script>
